// The finale's theatrical timing - the rollTheatre discipline applied to
// the end of the race. Bug report 2026-08-29: "include some time for drama,
// especially on point reveals or dice rolls - if the game goes by too
// quickly, you missed that and it isn't as much fun."
//
// Two moments used to flash by:
//
//   - the WINNING ROLL: the score sheet replaced the table on the very
//     frame the race ended, so nobody ever saw the camel cross the line;
//   - the FINAL REVEAL: standings and both betting decks appeared at once,
//     throwing away the game's whole payoff - the face-down cards ARE the
//     reveal, and they deserve to turn over one at a time.
//
// Like rollTheatre, this is theatre, not state: the engine has already
// scored everything, and every number here is display pacing only. All of
// it is skippable with a tap, and prefers-reduced-motion collapses it to
// instant - drama must never hold a player hostage.

import { rollAnimationMs } from './rollTheatre';

const defaultMatcher = typeof window !== 'undefined' ? window.matchMedia?.bind(window) : null;
const prefersReduced = (mediaMatcher) => Boolean(
  mediaMatcher?.('(prefers-reduced-motion: reduce)')?.matches,
);

// After the winning die lands: long enough to see the camel over the line
// and feel it, short enough that tapping through barely beats waiting.
export const FINALE_CROSS_MS = 1800;

// The score sheet's reveal beats. Cards are the slow ones on purpose - each
// is a face-down bet turning over, the closest thing this game has to a
// point reveal - while headers and standings rows move quicker so the
// sheet never drags.
export const REVEAL_RESULT_MS = 500;
export const REVEAL_HEADER_MS = 800;
export const REVEAL_CARD_MS = 900;
export const REVEAL_ROW_MS = 400;

// The leg report's stagger (CSS animation-delay per row, set by GameTable).
// Named here so the test can hold it against LEG_SCORED_PAUSE_MS: the bots'
// long read beat must outlast the last row's entrance at the table maximum.
export const LEG_REVEAL_BASE_MS = 250;
export const LEG_REVEAL_STEP_MS = 220;

/**
 * How long the TABLE stays on screen after the race-ending roll before the
 * score sheet takes over: the winning die's whole tumble, then a beat to
 * watch the camel sitting across the line. Zero unless the game is actually
 * finished, and zero under reduced motion.
 */
export function finaleHoldMs (game, mediaMatcher = defaultMatcher) {
  if (!game?.meta?.finalReport) return 0;
  if (prefersReduced(mediaMatcher)) return 0;
  // The race can only end on a roll, so the last event is always one and
  // rollAnimationMs knows its exact length.
  return rollAnimationMs(game, mediaMatcher) + FINALE_CROSS_MS;
}

/**
 * The score sheet's reveal as numbered steps: step 1 is the result line,
 * then each pile's header and its cards one by one (an empty pile's
 * "nobody bet" line is a beat too), then the standings rows - saving WHO
 * WON THE GAME for last, which is the drama the sheet builds to.
 *
 * Returns { at, result, piles, standings, total }:
 *   - at[i] is the ms offset at which step i+1 reveals (all zeros under
 *     reduced motion - everything shows at once);
 *   - result / piles[p].header / piles[p].cards / standings are the step
 *     numbers the template compares its reveal counter against (a pile's
 *     card i shows at step piles[p].cards + i; standings row i at
 *     standings + i);
 *   - total is the last step - reached, the sheet is fully dealt and the
 *     buttons come back.
 */
export function finalRevealPlan (report, mediaMatcher = defaultMatcher) {
  if (!report) {
    return { at: [], result: 1, piles: [], standings: 1, total: 0 };
  }

  const at = [];
  let clock = 0;
  const beat = (ms) => { clock += ms; at.push(clock); return at.length; };

  const result = beat(REVEAL_RESULT_MS);
  const piles = report.piles.map((pile) => {
    const header = beat(REVEAL_HEADER_MS);
    let cards = 0;
    const count = Math.max(pile.cards.length, 1);
    for (let i = 0; i < count; i += 1) {
      const step = beat(REVEAL_CARD_MS);
      if (i === 0) cards = step;
    }
    return { header, cards };
  });
  let standings = 0;
  report.standings.forEach((row, i) => {
    const step = beat(REVEAL_ROW_MS);
    if (i === 0) standings = step;
  });
  if (!standings) standings = at.length + 1; // degenerate: no standings rows

  const total = at.length;
  return {
    at: prefersReduced(mediaMatcher) ? at.map(() => 0) : at,
    result,
    piles,
    standings,
    total,
  };
}
